import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Toast, useToast } from '../context/ToastContext';

interface ToastDisplayProps {
  toast: Toast;
}

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const ToastContainer = styled.div<{ 
  $type: Toast['type'];
  $leaving?: boolean;
}>`
  position: relative;
  padding: ${props => props.$type === 'confirmation' ? '24px 28px' : '16px 20px'};
  margin-bottom: 12px;
  border-radius: ${props => props.$type === 'confirmation' ? '16px' : '14px'};
  background: ${props => {
    switch (props.$type) {
      case 'success': return 'linear-gradient(135deg, #1b5e20, #2e7d32)';
      case 'warning': return 'linear-gradient(135deg, #e65100, #f57c00)';
      case 'error': return 'linear-gradient(135deg, #c62828, #d32f2f)';
      case 'confirmation': return 'linear-gradient(135deg, #1565c0, #1976d2)';
      default: return 'linear-gradient(135deg, #424242, #616161)';
    }
  }};
  color: white;
  font-weight: 600;
  font-size: ${props => props.$type === 'confirmation' ? '16px' : '14px'};
  min-width: ${props => props.$type === 'confirmation' ? '300px' : '240px'};
  max-width: ${props => props.$type === 'confirmation' ? '400px' : '350px'};
  word-wrap: break-word;
  box-shadow: ${props => props.$type === 'confirmation' 
    ? '0 12px 28px -8px rgba(0,0,0,0.6), 0 4px 12px rgba(0,0,0,0.5)'
    : '0 8px 20px -6px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.4)'
  };
  border: 1px solid rgba(255,255,255,0.18);
  backdrop-filter: blur(14px) saturate(160%);
  animation: ${props => props.$leaving ? slideOut : slideIn} 0.4s ease forwards;
  pointer-events: auto;

  @media (max-width: 640px) {
    width: calc(100% - 32px);
    margin: 0 16px;
    font-size: 14px;
    
    /* Större touch-targets på mobil */
    button {
      min-height: 44px;
      min-width: 44px;
      padding: 10px 16px;
    }
  }
`;

const ToastContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ToastMessage = styled.div`
  line-height: 1.4;
`;

const ToastActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
`;

const ToastButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  background: ${props => {
    switch (props.$variant) {
      case 'danger': return 'rgba(211, 47, 47, 0.9)';
      case 'secondary': return 'rgba(97, 97, 97, 0.8)';
      default: return 'rgba(255, 255, 255, 0.2)';
    }
  }};
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 70px;

  &:hover {
    background: ${props => {
      switch (props.$variant) {
        case 'danger': return 'rgba(229, 57, 53, 0.9)';
        case 'secondary': return 'rgba(117, 117, 117, 0.8)';
        default: return 'rgba(255, 255, 255, 0.3)';
      }
    }};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    opacity: 1;
  }

  @media (max-width: 640px) {
    width: 36px;
    height: 36px;
    font-size: 24px;
  }
`;

const ToastIcon = styled.span`
  margin-right: 8px;
  font-size: 16px;
`;

const getToastIcon = (type: Toast['type']): string => {
  switch (type) {
    case 'success': return '✅';
    case 'warning': return '⚠️';
    case 'error': return '❌';
    case 'confirmation': return '❓';
    default: return 'ℹ️';
  }
};

export const ToastDisplay: React.FC<ToastDisplayProps> = ({ toast }) => {
  const { dismissToast } = useToast();

  const handleConfirm = () => {
    toast.confirmAction?.onConfirm();
  };

  const handleCancel = () => {
    toast.confirmAction?.onCancel?.();
  };

  const handleAction = () => {
    toast.action?.onClick();
  };

  const handleClose = () => {
    dismissToast(toast.id);
  };

  return (
    <ToastContainer $type={toast.type} $leaving={toast.leaving}>
      {/* Close button for non-confirmation toasts */}
      {toast.type !== 'confirmation' && (
        <CloseButton onClick={handleClose} aria-label="Stäng">
          ×
        </CloseButton>
      )}
      
      <ToastContent>
        <ToastMessage>
          <ToastIcon>{getToastIcon(toast.type)}</ToastIcon>
          {toast.message}
        </ToastMessage>
        
        {/* Confirmation buttons */}
        {toast.confirmAction && (
          <ToastActions>
            <ToastButton $variant="secondary" onClick={handleCancel}>
              {toast.confirmAction.cancelText || 'Avbryt'}
            </ToastButton>
            <ToastButton $variant="primary" onClick={handleConfirm}>
              {toast.confirmAction.confirmText || 'Ja'}
            </ToastButton>
          </ToastActions>
        )}
        
        {/* Single action button */}
        {toast.action && (
          <ToastActions>
            <ToastButton 
              $variant={toast.action.variant || 'primary'} 
              onClick={handleAction}
            >
              {toast.action.label}
            </ToastButton>
          </ToastActions>
        )}
      </ToastContent>
    </ToastContainer>
  );
};
